import { Component, OnInit } from '@angular/core';
import { ThemeCustomizerService } from '../../common/theme-customizer/theme-customizer.service';

interface DepartmentTab {
  id: string;
  label: string;
}

interface FactItem {
  title: string;
  value: string;
}

interface OutcomeItem {
  title: string;
  description: string;
}

interface LabItem {
  title: string;
  image: string;
  description: string;
}

interface ProgrammeItem {
  sno: number;
  name: string;
  duration: string;
}

interface AssociationDetailItem {
  sno: number;
  activity: string;
  date: string;
  description: string;
}

interface AssociationPhotoItem {
  image: string;
  caption: string;
}

interface MouItem {
  sno: number;
  organization: string;
  date: string;
  purpose: string;
}

interface MouPhotoItem {
  image: string;
  caption: string;
}

interface PlacementItem {
  sno: number;
  batch: string;
  studentName: string;
  designation: string;
  companyName: string;
}

interface IndustrialVisitItem {
  title: string;
  image: string;
  description: string;
}

interface ValueAddedCourseItem {
  sno: number;
  courseName: string;
  duration: string;
  companyName: string;
}

interface CoursePhotoItem {
  image: string;
  caption: string;
}

interface AlumniItem {
  name: string;
  batch: string;
  role: string;
  company: string;
  quote: string;
  image: string;
}

interface AchievementItem {
  sno: number;
  achiever: string;
  eventName: string;
  organizer: string;
  level: string;
  date: string;
  award: string;
}

interface AchievementPhotoItem {
  image: string;
  caption: string;
}

interface NptelRecordItem {
  staffName: string;
  subject: string;
  grade: string;
  session: string;
}

interface FdpItem {
  sno: number;
  staffName: string;
  title: string;
  collegeName: string;
  date: string;
}

interface ConferenceItem {
  sno: number;
  conferenceName: string;
  paperTitle: string;
  authors: string[];
  collegeName: string;
  date: string;
}

@Component({
  selector: 'app-courses-grid-style-one-columns-four-page',
  templateUrl: './courses-grid-style-one-columns-four-page.component.html',
  styleUrls: ['./courses-grid-style-one-columns-four-page.component.scss']
})
export class CoursesGridStyleOneColumnsFourPageComponent implements OnInit {
  isToggled = false;
  selectedTab = 'tab1';

  departmentName = 'Department of CSE (Artificial Intelligence & Machine Learning)';
  programmeSummary = '1 UG Programme';
  contactEmail = 'hodaiml@fmcet.ac.in';

  admissionNotice =
    'Admissions open for 2026 : B.E. (Civil), B.E. (CSE), B.E. (CSE - AI & ML), B.E. (CSE - CS), B.Tech. (AI & DS), B.E. (ECE), B.E. (EEE), B.E. (Mechanical), MBA, M.E. (CSE), M.E. (Embedded System Technologies), M.E. (Thermal Engineering), M.E. (Environmental Engineering)';

  tabs: DepartmentTab[] = [
    { id: 'tab1', label: 'About the Department' },
    { id: 'tab2', label: 'Vision & Mission' },
    { id: 'tab3', label: "PEO's" },
    { id: 'tab4', label: 'POs & PSOs' },
    { id: 'tab14', label: 'Programme Offered' },
    { id: 'tab5', label: 'Laboratory Facilities' },
    { id: 'tab6', label: 'Association Activities' },
    { id: 'tab7', label: "MoU's" },
    { id: 'tab8', label: 'Placement Record' },
    { id: 'tab9', label: 'Industrial Visit' },
    { id: 'tab10', label: 'Value Added Courses' },
    { id: 'tab11', label: 'Alumni' },
    { id: 'tab12', label: 'Achievements' },
    { id: 'tab13', label: 'NPTEL' },
    { id: 'tab15', label: 'Research' }
  ];

aboutParagraphs: string[] = [
  'The Department of Computer Science and Engineering (Artificial Intelligence and Machine Learning) was established in the academic year 2023 - 2024 with an initial intake of 30 students, which was increased to 60 students in the academic year 2024 - 2025. The department is committed to providing high-quality education, research, and innovation in AI and ML, giving students a strong foundation in computer science along with expertise in cutting-edge AI technologies.',
  'The four-year B.E. programme in CSE with a specialization in AI and ML prepares students for dynamic careers in industries driven by artificial intelligence and machine learning. The curriculum combines theoretical knowledge with practical applications, emphasizing workshops, internships, projects, case studies, and collaborative research.',
  'The department is equipped with AI-optimized laboratories, high-performance computing servers, industry-standard software tools, and high-speed internet access. Digital resources and e-learning platforms are integrated into the programme to strengthen conceptual understanding and support continuous skill development.',
  'With a research-focused approach and a dedicated faculty team, the department aims to develop future-ready professionals capable of leveraging AI and ML to solve real-world problems and contribute significantly to the global digital economy.'
];

aboutHighlights: string[] = [
  'Established in the academic year 2023 - 2024 with intake expanded from 30 to 60 students in 2024 - 2025.',
  'Strong foundation in Computer Science with advanced specialization in Artificial Intelligence and Machine Learning.',
  'AI-optimized laboratories, high-performance computing servers, software tools, and digital learning resources.',
  'Research-focused academic environment promoting innovation, projects, internships, and real-world problem solving.'
];

quickFacts: FactItem[] = [
  {
    title: 'Established',
    value: 'Academic Year 2023 - 2024'
  },
  {
    title: 'Programme',
    value: 'B.E. CSE (Artificial Intelligence & Machine Learning)'
  },
  {
    title: 'Intake',
    value: '60 Students'
  },
  {
    title: 'Academic Focus',
    value: 'Computer Science foundations with specialization in Artificial Intelligence, Machine Learning, research, innovation, and intelligent system development'
  }
];

visionText =
  'To be an eminent department in Artificial Intelligence and Machine Learning, recognized for excellence in education, research, and ethical practices, and for advancing innovative technologies and intelligent solutions that address future global challenges.';

missionPoints: string[] = [
  'To provide high-quality technical education in AI and ML with a strong foundation in computer science, equipping students with analytical, technical, and problem-solving skills.',
  'To promote research, innovation, and professional excellence, developing AI and ML solutions that contribute to societal and technological progress.',
  'To build a vibrant academic environment that promotes ethical values, adaptability, lifelong learning, and the application of AI and ML to solve real-world challenges responsibly.'
];

peos: string[] = [
  'PEO 1: Graduates will apply their technical expertise in Artificial Intelligence and Machine Learning to design, implement, and manage solutions for real-world problems, demonstrating analytical proficiency and leadership skills.',
  'PEO 2: Graduates will engage in research, innovation, and development of AI and ML solutions that address socially, technologically, and globally relevant challenges.',
  'PEO 3: Graduates will excel in professional and collaborative environments, demonstrating ethical conduct, adaptability, and lifelong learning while applying AI and ML technologies to solve practical and societal problems.'
];


  programOutcomes: OutcomeItem[] = [
    {
      title: 'Engineering Knowledge',
      description:
        'Apply the knowledge of mathematics, science, engineering fundamentals, and an engineering specialization to the solution of complex engineering problems.'
    },
    {
      title: 'Problem Analysis',
      description:
        'Identify, formulate, review research literature, and analyze complex engineering problems reaching substantiated conclusions using first principles of mathematics, natural sciences, and engineering sciences.'
    },
    {
      title: 'Design / Development of Solutions',
      description:
        'Design solutions for complex engineering problems and design system components or processes that meet the specified needs with appropriate consideration for public health, safety, cultural, societal, and environmental considerations.'
    },
    {
      title: 'Conduct Investigations of Complex Problems',
      description:
        'Use research-based knowledge and research methods including design of experiments, analysis and interpretation of data, and synthesis of information to provide valid conclusions.'
    },
    {
      title: 'Modern Tool Usage',
      description:
        'Create, select, and apply appropriate techniques, resources, and modern engineering and IT tools including prediction and modeling to complex engineering activities with an understanding of their limitations.'
    },
    {
      title: 'The Engineer and Society',
      description:
        'Apply reasoning informed by contextual knowledge to assess societal, health, safety, legal, and cultural issues and the consequent responsibilities relevant to professional engineering practice.'
    },
    {
      title: 'Environment and Sustainability',
      description:
        'Understand the impact of professional engineering solutions in societal and environmental contexts, and demonstrate the knowledge of, and need for sustainable development.'
    },
    {
      title: 'Ethics',
      description:
        'Apply ethical principles and commit to professional ethics, responsibilities, and norms of engineering practice.'
    },
    {
      title: 'Individual and Teamwork',
      description:
        'Function effectively as an individual, and as a member or leader in diverse teams and multidisciplinary settings.'
    },
    {
      title: 'Communication',
      description:
        'Communicate effectively on complex engineering activities with the engineering community and society at large through effective reports, design documentation, presentations, and clear instructions.'
    },
    {
      title: 'Project Management and Finance',
      description:
        'Demonstrate knowledge and understanding of engineering and management principles and apply these to one’s own work, as a member and leader in a team, to manage projects in multidisciplinary environments.'
    },
    {
      title: 'Life-long Learning',
      description:
        'Recognize the need for, and have the preparation and ability to engage in independent and life-long learning in the broadest context of technological change.'
    }
  ];

  programSpecificOutcomes: OutcomeItem[] = [
  {
    title: 'PSO 1',
    description:
      'Graduates will acquire advanced technical skills in AI and ML, enabling them to design, implement, and optimize intelligent systems and applications for real-world use.'
  },
  {
    title: 'PSO 2',
    description:
      'Graduates will leverage a strong theoretical foundation and research-oriented approach to develop innovative, practical, and socially responsible AI and ML solutions that contribute to technological and societal progress.'
  }
];

  programmes: ProgrammeItem[] = [
    {
      sno: 1,
      name: 'B.E. CSE (Artificial Intelligence & Machine Learning)',
      duration: '4 Years'
    }
  ];

  labs: LabItem[] = [
    {
      title: 'Data Science Lab',
      image: 'assets/img/aiml/l1.jpg',
      description:
        'Enables practical exploration of data analysis, visualization, predictive modeling, and intelligent data processing using tools such as Python, R, Pandas, NumPy, and Scikit-learn. Supports core learning in Data Science and Analytics.'
    },
    {
      title: 'Artificial Intelligence & Machine Learning Lab',
      image: 'assets/img/aiml/l3.jpg',
      description:
        'Provides practical exposure to machine learning algorithms, neural networks, AI models, and deep learning frameworks using Python, TensorFlow, and Keras. Supports courses related to Artificial Intelligence and Machine Learning.'
    },
    {
      title: 'Computer Networks Lab',
      image: 'assets/img/aiml/l2.jpg',
      description:
        'Enables hands-on learning in routing, switching, protocols, network configuration, and communication concepts using simulators and real networking devices. Supports Computer Networks and related laboratory work.'
    }
  ];

  associationDetailRecords: AssociationDetailItem[] = [
    {
      sno: 1,
      activity: 'Online Student Seminar',
      date: '09.01.2026',
      description:
        'Organized by AI Innovators Club with M. Vishal as Resource Person. Total participants: 40.'
    },
    {
      sno: 2,
      activity: 'Online Student Seminar',
      date: '13.02.2026',
      description:
        'Organized by AI Research Club with S. Yazhini as Resource Person. Total participants: 43.'
    },
    {
      sno: 3,
      activity: 'Tech and Fun Events',
      date: '20.03.2026',
      description:
        'Organized by Brainy Bots Club with Abulhasan J as Resource Person. Total participants: 38.'
    }
  ];

  associationPhotos: AssociationPhotoItem[] = [
    {
      image: 'assets/img/aiml/aa1.jpg',
      caption: ''
    },
    {
      image: 'assets/img/aiml/aa2.jpg',
      caption: ''
    }
  ];

  mous: MouItem[] = [
    {
      sno: 1,
      organization: 'Reccsar Private Limited',
      date: '12.02.2026',
      purpose: 'Placement, Industrial Visit and Internship'
    }
  ];

  mouPhotos: MouPhotoItem[] = [
    {
      image: 'assets/img/aiml/mou1.jpg',
      caption: 'MoU Signed with Reccsar Private Limited on 12.02.2026'
    },

  ];

  placementRecords: PlacementItem[] = [];

  industrialVisits: IndustrialVisitItem[] = [
    {
      title: 'External Practical Learning (EPL) at Tandem Informatics, Madurai',
      image: 'assets/img/aiml/iv1.jpg',
      description:
        'On 04.10.2025, the department organized an External Practical Learning visit at Tandem Informatics, Madurai, for III Year students to provide practical industry exposure and understanding of real-time software and technology working environments.'
    },
    {
      title: 'Educational and Industrial Visit at HCL Tech, Madurai',
      image: 'assets/img/aiml/iv2.jpg',
      description:
        'On 27.01.2026, a one-day educational and industrial visit was organized at HCL Tech, Madurai, for II and III Year students to enhance industrial exposure and understanding of professional practices, technology workflows, and corporate work culture.'
    },
    {
      title: 'Visit to HCL Tech Campus, Madurai',
      image: 'assets/img/aiml/iv3.jpg',
      description:
        'Students gained direct insight into professional technology environments, industrial standards, and workplace interaction through the educational visit to HCL Tech, Madurai.'
    },
    {
      title: 'Staff Industrial Training at Hebesec Technologies, Karaikudi',
      image: 'assets/img/aiml/iv4.jpg',
      description:
        'A one-day staff industrial training programme was organized at Hebesec Technologies, Karaikudi, to strengthen faculty exposure to current industrial practices and emerging technology trends.'
    }
  ];

  valueAddedCourses: ValueAddedCourseItem[] = [
    {
      sno: 1,
      courseName: 'Machine Learning Using Python',
      duration: '19.01.2026 to 23.01.2026',
      companyName: 'Reccsar Private Limited'
    },
    {
      sno: 2,
      courseName: 'Generative AI',
      duration: '19.01.2026 to 23.01.2026',
      companyName: 'Pantech ProEd Private Limited'
    }
  ];

  valueAddedCoursePhotos: CoursePhotoItem[] = [
    {
      image: 'assets/img/aiml/vac2.jpg',
      caption: 'Machine Learning Using Python'
    },
    {
      image: 'assets/img/aiml/vac1.jpg',
      caption: 'Generative AI'
    }
  ];

  alumniRecords: AlumniItem[] = [];

  achievements: AchievementItem[] = [
    {
      sno: 1,
      achiever: 'S. Jenitta – III Year AIML, J. Jeya Suriya Devi – III Year AIML',
      eventName: "Symposium - ALCONESY’25 (Project Expo Event)",
      organizer: 'Alagappa Chettiyar Government College of Engineering and Technology',
      level: 'State',
      date: '29.10.2025 & 30.10.2025',
      award: 'Second Prize'
    },
    {
      sno: 2,
      achiever: 'S. Saravana Priyan – II Year AIML, S. Maruthu Pandi – II Year AIML',
      eventName: 'Symposium - Innovatex 2025 (Code Debug)',
      organizer: 'Kamaraj College of Engineering and Technology',
      level: 'State',
      date: '17.10.2025',
      award: 'Third Prize'
    },
    {
      sno: 3,
      achiever: 'S. Jenitta – III Year AIML',
      eventName: 'Milan 2026 Context',
      organizer: 'Mangayarkarasi College of Engineering, Madurai',
      level: 'State',
      date: '06.03.2026',
      award: 'Second Prize'
    }
  ];

  achievementPhotos: AchievementPhotoItem[] = [
    {
      image: 'assets/img/aiml/achi1.jpeg',
      caption: ''
    },

  ];

  nptelRecords: NptelRecordItem[] = [
    {
      staffName: 'Dr. S. Mathumohan',
      subject: 'OBE and Accreditation',
      grade: 'Elite + Silver',
      session: '2025 - 2026'
    },
    {
      staffName: 'Mrs. K. Maheswari',
      subject: 'OBE and Accreditation',
      grade: 'Pass',
      session: '2025 - 2026'
    }
  ];

  fdpRecords: FdpItem[] = [];

  conferenceRecords: ConferenceItem[] = [
    {
      sno: 1,
      conferenceName: 'International Conference on Information Systems and Computer Networks (ISCON)',
      paperTitle: 'Air Purification Robotics using Cloud and Deep Q Networks for Autonomous Systems',
      authors: [
        'Priyanka Kaushik',
        'Pawan Kumar',
        'S. Mathumohan'
      ],
      collegeName: 'VIT University, Chennai',
      date: '04.03.2026'
    }
  ];

  constructor(public themeService: ThemeCustomizerService) {
    this.themeService.isToggled$.subscribe((isToggled) => {
      this.isToggled = isToggled;
    });
  }

  get activeTabLabel(): string {
    return (
      this.tabs.find((tab) => tab.id === this.selectedTab)?.label ||
      'Department Profile'
    );
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
  }

  ngOnInit(): void {}
}